// eslint-disable-next-line no-unused-vars
import {ObjectSchema, reach} from 'yup';

import * as dataUtils from '../../utils/dataUtils';


class Form {

    /**
     * @type {object} Field name to FormField
     */
    data = {};

    /**
     * @type {string}
     */
    id;

    /**
     * @type {object} Initial data during instantiation
     */
    initialData = {};

    /**
     * @type {function} Called after data has changed and validated
     */
    onChangeHook = () => {};

    /**
     * @type {ObjectSchema} Yup schema
     */
    schema = null;

    /**
     * @type {object} General validation results
     */
    validationResults = {
        allErrors: {},
        currentErrors: {}
    };


    /**
     * @param id {string} Form identifier
     * @param schema {ObjectSchema} Yup schema to use for storage
     * @param data {object} Initial data
     */
    constructor(id, schema, data = {}) {

        this.id = id;
        this.schema = schema;

        dataUtils.each(schema.fields, (fieldName, fieldSchema) => {

            let fieldValue = fieldSchema.getDefault();
            if (data.hasOwnProperty(fieldName)) {
                fieldValue = data[fieldName];
            }

            this.data[fieldName] = fieldValue;
            this.initialData[fieldName] = fieldValue;
        });

        this.validate({});
    }


    /**
     * @param path {string|null} Field name or path to get value for
     * @param defaultValue {any|undefined} Default value in case the field doesn't exist
     * @param context {object} Data context to look in
     * @return {object} Field name to values
     */
    getData(path = null, defaultValue = undefined, context = this.data) {

        if (!path) {

            return {...context};
        }

        return dataUtils.findInObject(context, path, defaultValue);
    }


    /**
     * @param changedData {object} Data to set
     */
    setData(changedData) {

        dataUtils.each(changedData, (fieldName, value) => {
            this.data[fieldName] = value;
        });
        this.validate(changedData);
    }


    /**
     * @param changedData {object} Field name to new value
     */
    mergeData(changedData) {

        this.data = dataUtils.merge(this.data, changedData);
        this.validate(changedData);
    }


    /**
     * @param changedData {Object} Data that got changed
     */
    validate(changedData) {

        (async () => {

            let allErrors = {};
            try {

                const finalMergedData = dataUtils.merge(this.data, changedData);
                // this.schema.validateSync(finalMergedData, {
                await this.schema.validate(finalMergedData, {
                    abortEarly: false
                });

            } catch (validationErrors) {
                allErrors = this.convertToFieldErrors(validationErrors);
            }

            this.validationResults = this.createValidationResults(changedData, allErrors);
            this.onChangeHook();

        })();
    }


    convertToFieldErrors(validationError) {

        const allErrors = {};
        if (validationError.path) {
            allErrors[validationError.path] = this.convertToFieldError(validationError);
        }
        validationError.inner.forEach(innerValidationError => {
            allErrors[innerValidationError.path] = this.convertToFieldError(innerValidationError);
        });

        return allErrors;
    }


    convertToFieldError(validationError) {

        return {
            message: validationError.message,
            params: validationError.params,
            type: validationError.type,
            value: validationError.message
        };
    }


    /**
     * @param changedData {Object} Changed data
     * @param allErrors {Object} All errors, keyed by field name
     * @return {Object} Object providing all errors, previous and current errors and final validity state
     */
    createValidationResults(changedData, allErrors = {}) {

        // errors of current change
        const changedDataPaths = Object.keys(dataUtils.flattenObject(changedData));
        const newCurrentErrors = dataUtils.retainKeys(allErrors, changedDataPaths);

        // remove errors if a delta between old and new current errors exist
        const previousErrors = this.validationResults.currentErrors;
        const removedErrorPaths = dataUtils.getMissingKeys(previousErrors, allErrors);
        const cleanedPreviousErrors = dataUtils.removeKeys(previousErrors, removedErrorPaths);

        // add errors of current change to previous clean up
        const currentErrors = dataUtils.merge(cleanedPreviousErrors, newCurrentErrors);

        return {
            allErrors,
            currentErrors
        };
    }


    /**
     * @param formId {string} Form id used as i18n prefix
     * @param validationError {object} Validation error
     * @param path {null|string} Optional path definition if going cross-structure-form
     * @return {string} I18n error label
     */
    buildErrorLabel(formId, validationError, path = null) {

        const {message} = validationError;
        if (message && message.startsWith('i18n:')) {

            return message.replace('i18n:', '');
        }

        const fieldName = path || validationError.params.path;

        return `${formId}.field.${fieldName}.error.${validationError.type}`;
    }


    /**
     * @param errorContainer {object} Error container (current or all)
     * @param fieldName {string} Field name to get error for
     * @param formId {string} Error i18n label prefixing, defaults to set form id
     * @param fieldPath {string|null} Error message field path to override auto-construction
     * @return {object|null} Error if present; falsy if not
     */
    #constructError(errorContainer, fieldName, formId = this.id, fieldPath = null) {

        const error = errorContainer[fieldName];
        if (!error) {

            return null;
        }
        error.label = this.buildErrorLabel(formId, error, fieldPath);

        return error;
    }


    /**
     * @param fieldName {string} Field name to get error for
     * @param formId {string} Error i18n label prefixing, defaults to set form id
     * @param fieldPath {string|null} Error message field path to override auto-construction
     * @return {object|null} Error if present; falsy if not
     */
    getCurrentError(fieldName, formId = this.id, fieldPath = null) {

        return this.#constructError(this.validationResults.currentErrors, fieldName, formId, fieldPath);
    }


    /**
     * @param fieldName {string} Field name to check for error in current field validation
     * @return {Boolean} True if a current error is set for the given field; false if not
     */
    hasCurrentError(fieldName) {

        const error = this.getCurrentError(fieldName);

        return Boolean(error);
    }


    /**
     * @param fieldName {string} Field name to get error for
     * @param formId {string} Error i18n label prefixing, defaults to set form id
     * @param fieldPath {string|null} Error message field path to override auto-construction
     * @return {object|null} Error if present; falsy if not
     */
    getError(fieldName, formId = this.id, fieldPath = null) {

        return this.#constructError(this.validationResults.allErrors, fieldName, formId, fieldPath);
    }


    /**
     * @param fieldName {string} Field name to check for error
     * @return {Boolean} True if a current error is set for the given field; false if not
     */
    hasError(fieldName) {

        return this.validationResults.allErrors.hasOwnProperty(fieldName);
    }


    /**
     * @return {Boolean} True if any current error exist; false if not
     */
    hasErrors() {

        return Object.values(this.validationResults.allErrors).length > 0;
    }


    /**
     * @return {{}} All errors
     */
    getErrors() {

        return this.validationResults.allErrors;
    }


    /**
     * @return {boolean} True if the current data differs (deep equality check) from the initially provided data
     */
    isModified() {

        return dataUtils.isDifferent(this.data, this.initialData);
    }


    /**
     * @return {Boolean} True if the form is valid/no errors exist; false if invalid/at least one error exists
     */
    isValid() {

        return this.hasErrors() === false;
    }


    /**
     * @param path {string} Path to a field to check whether it is required or not
     * @return {boolean} True if field is required; false if not
     */
    isRequired(path) {

        // const fieldSpec = dataUtils.findInObject(this.schema.fields, path, null, (entry) => entry.fields);
        const fieldSpec = reach(this.schema, path);
        if (!fieldSpec) {

            throw Error(`Form ${this.id} has no such field: ${path}`);
        }

        return fieldSpec.spec.presence === 'required';
    }
}


export default Form;
